import { Badge, Card, Grid, Group, Text } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import React from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { CreateSuperAdminThreadAction } from "src/components/superadmin/threads/SuperAdminThreadActions"
import { makeEnumBadgeComponent } from "src/components/ui/Badges"
import { DataTable } from "src/components/ui/DataTable"
import { LoadMoreButton } from "src/components/ui/LoadMoreButton"
import { EmptyPageComponent } from "src/components/ui/Misc"
import { ModuleErrors } from "src/components/ui/ModuleErrors"
import { PageFilters, usePageFilters } from "src/components/ui/PageFilters"
import { PageHeader } from "src/components/ui/PageHeader"
import { PageLayout } from "src/components/ui/PageLayout"
import { useActions, useAppState } from "src/state"
import { SetMetaInput } from "src/state/Module"
import { ThreadFragment, Thread_Status_Enum, Thread_Type_Enum, Threads_Bool_Exp } from "src/state/gql/_types"
import { formatInboxDate } from "src/utils/dates"
import { createClickFilterEnumValues } from "src/components/ui/PageFilters"

const clickFilterEnumValues = createClickFilterEnumValues<Thread_Status_Enum, Threads_Bool_Exp>(Thread_Status_Enum, 'status')

export const ThreadStatusTag = makeEnumBadgeComponent(Thread_Status_Enum)
export const ThreadTypeTag = makeEnumBadgeComponent(Thread_Type_Enum,'outline')

export function SuperAdminThreads() {

    const location = useLocation()
    const navigation = useNavigate()
    const { itemsList, errors, meta, loadingMore, loading, meta: { limit, offset, sortKey, sortOrder } } = useAppState(state => state.threads)
    const {
        subscribeToItems,
        unsubscribeFromItems,
        getMoreItems,
        resetModule,
        setMeta,
    } = useActions().threads
    const { view } = useAppState(state => state.ui)

    const {
        filters,
        filterParams
    } = usePageFilters<Threads_Bool_Exp['_and'], ThreadFragment>({
        baseFilters: [],
        clickFilters: [
            {
                _or: [
                    { status: { _eq: Thread_Status_Enum.Open } }
                ]
            }
        ],
        searchFilterFields: [],
        searchFilterRelations: {},
        clickFilterField: 'status',
        clickFiltersDisableDefaultAll: true,
        clickFilterDefault: 'open',
        clickFilterValues: clickFilterEnumValues
    })

    React.useEffect(() => {
        subscribeToItems(filters);
        return () => {
            unsubscribeFromItems()
        }
    }, [filters, limit, offset, sortKey, sortOrder, subscribeToItems, unsubscribeFromItems, resetModule])

    const columnHelper = createColumnHelper<ThreadFragment>()

    const columns = React.useMemo(() => [
        columnHelper.accessor('id', {
            header: 'Id'
        }),
        columnHelper.accessor(row => row.thread_status, {
            id: 'status',
            meta: {
                relationKey: 'thread_status',
                sortKey: 'value'
            },
            header: 'Status',
            cell: row => <ThreadStatusTag status={row.getValue()!} />
        }),
        columnHelper.accessor(row => row.thread_type, {
            id: 'type',
            meta: {
                relationKey: 'thread_type',
                sortKey: 'value'
            },
            header: 'Type',
            cell: row => <ThreadTypeTag status={row.getValue()!} />
        }),
        columnHelper.accessor('subject', {
            header: 'Subject'
        }),
        columnHelper.accessor('unread_count', {
            header: 'Unread'
        }),
        columnHelper.accessor(row => `${row.created_by_user?.first_name} ${row.created_by_user?.last_name}`, {
            id: 'created_by_name',
            meta: {
                relationKey: 'created_by_user',
                sortKey: 'first_name'
            },
            header: 'Created By'
        }),
        columnHelper.accessor('created_at', {
            header: 'Created At',
            cell: row => formatInboxDate(row.getValue())
        }),
    ], [columnHelper])

    const initialState = {
        columnVisibility: {
            id: false
        }
    }

    const handleMetaChange = (meta: SetMetaInput<ThreadFragment>) => {
        setMeta(meta)
    }

    const handleSelect = (id: string) => {
        navigation(`/inbox/${id}`, { state: { drawerFrom: location } })
    }

    const renderCards = () => {
        return (
            <React.Fragment>
                <Grid>
                    {itemsList.map((i, k) => {
                        return (
                            <Grid.Col span={6} key={k}>
                                <Card
                                    component={Link}
                                    to={`/threads/${i.id}`}
                                    shadow="xs"
                                    className="card-theme-global"
                                >
                                    <Group justify='space-between'>
                                        <Text fw={500}>#{i.id}</Text>
                                        <Badge variant="outline">{i.status}</Badge>
                                    </Group>
                                    <Text size="sm">{i.id}</Text>
                                </Card>
                            </Grid.Col>
                        )
                    })}
                </Grid>
                <LoadMoreButton meta={meta} onLoadMore={getMoreItems} loading={loadingMore} />
            </React.Fragment>
        )
    }

    const renderList = () => {
        return (
            <DataTable<ThreadFragment>
                columns={columns}
                data={itemsList}
                loading={loading}
                meta={meta}
                onMetaChange={handleMetaChange}
                initialState={initialState}
                onSelectItem={handleSelect}
            />
        )
    }

    return (
        <PageLayout
            header={
                <PageHeader
                    title={`Threads`}
                    renderAction={
                        <Group>
                            <CreateSuperAdminThreadAction />
                        </Group>
                    }
                />
            }
        >
            <PageFilters options={filterParams} />
            <ModuleErrors errors={errors} />
            {itemsList.length ? (<> {view === 'list' ? renderList() : renderCards()} </>) : (<EmptyPageComponent label='Threads' />)}
        </PageLayout>
    )

}